import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plane, Key, Upload, Trash2, Copy, Clock, MapPin, Gauge, ArrowUpDown, FileText, Plus, Activity, Link2, BarChart3, AlertCircle, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { projectsAPI } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const FlightDataPage = () => {
  const { user, token, isSuperAdmin } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canManage = isSuperAdmin || isAdmin;

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [tokens, setTokens] = useState([]);
  const [flights, setFlights] = useState([]);
  const [stats, setStats] = useState(null);
  const [showNewToken, setShowNewToken] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('flights'); // flights | tokens | upload

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await projectsAPI.list();
        setProjects(res.data);
        if (res.data.length > 0) {
          const userP = res.data.find(p => p.id === user?.project_id);
          setSelectedProject(userP ? userP.id : res.data[0].id);
        }
      } catch (e) { console.error(e); }
    };
    fetchProjects();
  }, [user?.project_id]);

  const fetchFlights = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const [fRes, sRes] = await Promise.all([
        fetch(`${API}/api/flight-data/${selectedProject}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/flight-data-stats/${selectedProject}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (fRes.ok) setFlights(await fRes.json());
      if (sRes.ok) setStats(await sRes.json());
    } catch (e) { console.error(e); }
  }, [selectedProject, token]);

  const fetchTokens = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/flight-tokens`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTokens(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchFlights(); }, [fetchFlights]);
  useEffect(() => { if (canManage) fetchTokens(); }, [canManage, fetchTokens]);

  const createToken = async () => {
    if (!selectedProject || !tokenName.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/flight-tokens`, {
        method: 'POST', headers,
        body: JSON.stringify({ project_id: selectedProject, name: tokenName.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setCreatedToken(data);
        setTokenName('');
        fetchTokens();
        toast.success('Token criado!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao criar token');
      }
    } catch { toast.error('Erro de conexao'); }
    setLoading(false);
  };

  const revokeToken = async (tokenId) => {
    if (!confirm('Revogar este token? Apps usando-o perderao acesso.')) return;
    try {
      const res = await fetch(`${API}/api/flight-tokens/${tokenId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { fetchTokens(); toast.success('Token revogado'); }
    } catch { toast.error('Erro'); }
  };

  const deleteFlight = async (flightId) => {
    if (!confirm('Excluir este registro de voo?')) return;
    try {
      const res = await fetch(`${API}/api/flight-data/${flightId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { fetchFlights(); toast.success('Registro excluido'); }
    } catch { toast.error('Erro'); }
  };

  const uploadLog = async () => {
    if (!uploadFile || !selectedProject) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('project_id', selectedProject);
    try {
      const res = await fetch(`${API}/api/flight-data/upload-log`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setUploadFile(null);
        fetchFlights();
      } else {
        toast.error(data.detail || 'Erro ao processar arquivo');
      }
    } catch { toast.error('Erro de conexao'); }
    setLoading(false);
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copiado!'); };

  const formatDuration = (sec) => {
    if (!sec) return '0s';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.round(sec % 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const pwaUrl = `${window.location.origin}/pwa-flight`;
  const projectTokens = tokens.filter(t => t.project_id === selectedProject);

  return (
    <div className="space-y-6" data-testid="flight-data-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <Plane size={20} className="text-gold" /> DADOS DE VOO
        </h2>
        <div className="flex items-center gap-3">
          {(isSuperAdmin || projects.length > 1) && (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-[200px] input-hud text-xs h-8" data-testid="flight-project-select">
                <SelectValue placeholder="Projeto" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="flight-stats">
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Total Voos</p>
            <p className="text-2xl font-rajdhani font-bold text-navy">{stats.total_flights}</p>
          </div>
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Horas Voadas</p>
            <p className="text-2xl font-rajdhani font-bold text-blue-600">{stats.total_duration_hours}h</p>
          </div>
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Distancia (km)</p>
            <p className="text-2xl font-rajdhani font-bold text-green-600">{stats.total_distance_km}</p>
          </div>
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Alt. Max (m)</p>
            <p className="text-2xl font-rajdhani font-bold text-amber-600">{stats.max_altitude_meters || 0}</p>
          </div>
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Tokens Ativos</p>
            <p className="text-2xl font-rajdhani font-bold text-gold">{projectTokens.length}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-1">
        {[
          { key: 'flights', label: 'REGISTROS DE VOO', icon: Activity },
          ...(canManage ? [{ key: 'tokens', label: 'TOKENS', icon: Key }] : []),
          { key: 'upload', label: 'UPLOAD LOG', icon: Upload },
          ...(canManage ? [{ key: 'pwa', label: 'APP CONTROLE', icon: ExternalLink }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-rajdhani font-semibold uppercase tracking-wider transition-colors rounded-t-lg ${
              tab === t.key ? 'text-gold border-b-2 border-gold bg-gold/5' : 'text-gray-400 hover:text-navy'
            }`} data-testid={`tab-${t.key}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Flights */}
      {tab === 'flights' && (
        <div className="card-hud overflow-hidden" data-testid="flights-table">
          {flights.length === 0 ? (
            <div className="p-8 text-center">
              <Plane size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-mono text-sm">Nenhum dado de voo registrado</p>
              <p className="text-gray-300 text-xs mt-1">Use o app PWA no controle ou faca upload de logs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Data</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Drone</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Piloto</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Duracao</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Distancia</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Alt. Max</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Origem</th>
                    {canManage && <th className="text-right p-3 text-gray-500 font-mono text-[10px] uppercase">Acoes</th>}
                  </tr>
                </thead>
                <tbody>
                  {flights.map((f, idx) => (
                    <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`flight-row-${idx}`}>
                      <td className="p-3 text-xs text-navy font-medium">{formatDate(f.start_time)}</td>
                      <td className="p-3 text-xs text-gray-600">{f.drone_serial || f.drone_model || '-'}</td>
                      <td className="p-3 text-xs text-gray-600">{f.pilot_name || '-'}</td>
                      <td className="p-3 text-xs font-mono text-blue-600">{formatDuration(f.duration_seconds)}</td>
                      <td className="p-3 text-xs font-mono text-green-600">{f.distance_meters > 1000 ? `${(f.distance_meters / 1000).toFixed(1)}km` : `${Math.round(f.distance_meters)}m`}</td>
                      <td className="p-3 text-xs font-mono text-amber-600">{f.max_altitude_meters ? `${f.max_altitude_meters}m` : '-'}</td>
                      <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${f.source === 'pwa_app' ? 'bg-gold/10 text-gold' : f.source === 'csv_upload' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>{f.source}</span></td>
                      {canManage && (
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-300 hover:text-red-500" onClick={() => deleteFlight(f.id)} data-testid={`delete-flight-${idx}`}>
                            <Trash2 size={12} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Tokens */}
      {tab === 'tokens' && canManage && (
        <div className="space-y-4">
          <div className="card-hud p-4 flex items-center justify-between">
            <div>
              <p className="text-navy font-rajdhani font-semibold text-sm uppercase">Tokens de Projeto</p>
              <p className="text-gray-400 text-xs">Gere tokens para autenticar o envio de dados do controle remoto</p>
            </div>
            <Button onClick={() => setShowNewToken(true)} className="bg-gold hover:bg-gold/90 text-navy text-xs h-8" data-testid="new-token-btn">
              <Plus size={14} className="mr-1" /> NOVO TOKEN
            </Button>
          </div>

          {createdToken && (
            <div className="card-hud p-4 border-2 border-gold/30 bg-gold/5" data-testid="created-token-display">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-gold mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-navy font-semibold text-sm mb-1">Token criado: {createdToken.name}</p>
                  <p className="text-gray-400 text-[10px] mb-2">Copie agora. Este token nao sera exibido novamente.</p>
                  <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-2">
                    <code className="text-xs font-mono text-navy flex-1 break-all" data-testid="token-value">{createdToken.token}</code>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gold hover:bg-gold/10 flex-shrink-0" onClick={() => copyToClipboard(createdToken.token)} data-testid="copy-token-btn">
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card-hud overflow-hidden">
            {projectTokens.length === 0 ? (
              <div className="p-8 text-center">
                <Key size={32} className="text-gray-200 mx-auto mb-2" />
                <p className="text-gray-400 text-xs">Nenhum token para este projeto</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {projectTokens.map((t, idx) => (
                  <div key={t.id} className="flex items-center justify-between p-4 hover:bg-gray-50" data-testid={`token-row-${idx}`}>
                    <div>
                      <p className="text-navy font-medium text-sm flex items-center gap-2">
                        <Key size={12} className="text-gold" /> {t.name}
                      </p>
                      <p className="text-gray-400 text-[10px] font-mono mt-0.5">
                        {t.token.substring(0, 12)}... | Criado: {formatDate(t.created_at)} | Usos: {t.usage_count}
                        {t.last_used && ` | Ultimo: ${formatDate(t.last_used)}`}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-gray-300 hover:text-red-500 text-xs" onClick={() => revokeToken(t.id)} data-testid={`revoke-token-${idx}`}>
                      <Trash2 size={12} className="mr-1" /> Revogar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Upload */}
      {tab === 'upload' && (
        <div className="card-hud p-6 space-y-4" data-testid="upload-log-section">
          <div className="flex items-start gap-3">
            <Upload size={20} className="text-gold mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-navy font-rajdhani font-semibold text-sm uppercase">Upload de Log de Telemetria</p>
              <p className="text-gray-400 text-xs">Carregue arquivos .csv ou .txt de telemetria DJI para extrair dados de voo automaticamente</p>
            </div>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gold/50 transition-colors cursor-pointer"
            onClick={() => document.getElementById('log-file-input').click()}>
            <input id="log-file-input" type="file" accept=".csv,.txt" className="hidden"
              onChange={(e) => setUploadFile(e.target.files[0])} data-testid="log-file-input" />
            <FileText size={32} className="text-gray-300 mx-auto mb-2" />
            {uploadFile ? (
              <p className="text-navy font-medium text-sm">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)</p>
            ) : (
              <p className="text-gray-400 text-sm">Clique para selecionar arquivo .csv ou .txt</p>
            )}
          </div>
          {uploadFile && (
            <Button onClick={uploadLog} disabled={loading} className="bg-gold hover:bg-gold/90 text-navy w-full h-10" data-testid="upload-log-btn">
              {loading ? 'Processando...' : 'PROCESSAR E IMPORTAR'}
            </Button>
          )}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-navy text-xs font-semibold flex items-center gap-1"><AlertCircle size={12} className="text-gold" /> Formatos suportados:</p>
            <ul className="text-gray-500 text-[11px] space-y-1 ml-4">
              <li>CSV com colunas de tempo, distancia, altitude, velocidade</li>
              <li>TXT com logs de telemetria DJI (timestamps, duracao, distancia)</li>
              <li>Logs de voo exportados do DJI Go / DJI Fly / Autel Explorer</li>
            </ul>
          </div>
        </div>
      )}

      {/* Tab: PWA App */}
      {tab === 'pwa' && canManage && (
        <div className="card-hud p-6 space-y-4" data-testid="pwa-section">
          <div className="flex items-start gap-3">
            <Plane size={20} className="text-gold mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-navy font-rajdhani font-semibold text-sm uppercase">App para Controle Remoto (PWA)</p>
              <p className="text-gray-400 text-xs">Abra o link abaixo no navegador do controle Android (DJI RC Pro, Autel Smart Controller, etc.)</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-navy/5 rounded-xl border border-navy/10 p-3">
            <Link2 size={14} className="text-gold flex-shrink-0" />
            <code className="text-xs font-mono text-navy flex-1 break-all">{pwaUrl}</code>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-gold hover:bg-gold/10 flex-shrink-0" onClick={() => copyToClipboard(pwaUrl)} data-testid="copy-pwa-url">
              <Copy size={14} />
            </Button>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-navy text-xs font-semibold">Instrucoes de uso:</p>
            <ol className="text-gray-500 text-[11px] space-y-1.5 ml-4 list-decimal">
              <li>Gere um token de projeto na aba TOKENS</li>
              <li>Abra o link acima no navegador Chrome do controle remoto</li>
              <li>Cole o token e selecione o drone</li>
              <li>Registre os voos manualmente ou use o timer automatico</li>
              <li>Os dados serao enviados automaticamente para a plataforma</li>
            </ol>
          </div>
          <Button onClick={() => window.open(pwaUrl, '_blank')} className="bg-gold hover:bg-gold/90 text-navy w-full h-10" data-testid="open-pwa-btn">
            <ExternalLink size={14} className="mr-2" /> ABRIR APP PWA
          </Button>
        </div>
      )}

      {/* New Token Dialog */}
      <Dialog open={showNewToken} onOpenChange={setShowNewToken}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-navy font-rajdhani">Novo Token de Projeto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-gray-500 text-xs font-mono uppercase block mb-1">Nome do Token</label>
              <Input value={tokenName} onChange={e => setTokenName(e.target.value)} placeholder="Ex: Controle DJI RC Pro - Piloto Joao" className="input-hud" data-testid="token-name-input" />
            </div>
            <div>
              <label className="text-gray-500 text-xs font-mono uppercase block mb-1">Projeto</label>
              <p className="text-navy font-medium text-sm">{projects.find(p => p.id === selectedProject)?.name || '-'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewToken(false)}>Cancelar</Button>
            <Button onClick={() => { createToken(); setShowNewToken(false); }} disabled={!tokenName.trim() || loading} className="bg-gold hover:bg-gold/90 text-navy" data-testid="confirm-create-token">Criar Token</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlightDataPage;
